import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loader from '../../components/common/Loader';
import './PaymentSettings.css';

const PaymentSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [bogSettings, setBogSettings] = useState({
    enabled: false,
    settings: { client_id: '', secret_key: '' }
  });

  const [bankSettings, setBankSettings] = useState({
    enabled: false,
    settings: {
      bank_name: 'Bank of Georgia',
      iban: '',
      account_holder: '',
      swift: 'BAGAGE22'
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/payment-settings');
      
      if (response.data.success) {
        const settings = response.data.data;
        
        settings.forEach(setting => {
          // ✅ FIXED: No JSON.parse needed - backend already returns objects
          const settingsData = typeof setting.settings === 'object' 
            ? setting.settings 
            : {};

          if (setting.payment_method === 'bog_ipay') {
            setBogSettings({
              enabled: setting.enabled,
              settings: settingsData
            });
          } else if (setting.payment_method === 'bank_transfer') {
            setBankSettings({
              enabled: setting.enabled,
              settings: settingsData
            });
          }
        });
      }
    } catch (error) {
      console.error('Fetch settings error:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (method, data) => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.put(`/admin/payment-settings/${method}`, data);
      
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Save settings error:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleBogToggle = async () => {
    const newEnabled = !bogSettings.enabled;
    setBogSettings({ ...bogSettings, enabled: newEnabled });
    await saveSettings('bog_ipay', {
      enabled: newEnabled,
      settings: bogSettings.settings
    });
  };

  const handleBogSave = async () => {
    await saveSettings('bog_ipay', bogSettings);
  };

  const handleBankToggle = async () => {
    const newEnabled = !bankSettings.enabled;
    setBankSettings({ ...bankSettings, enabled: newEnabled });
    await saveSettings('bank_transfer', {
      enabled: newEnabled,
      settings: bankSettings.settings
    });
  };

  const handleBankSave = async () => {
    await saveSettings('bank_transfer', bankSettings);
  };

  if (loading) return <Loader fullscreen />;

  return (
    <div className="payment-settings-page">
      <div className="page-header">
        <h1 className="page-title">💳 Payment Settings</h1>
        <p className="page-subtitle">Configure payment methods for your store</p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* BOG iPay Settings */}
      <div className="payment-method-card">
        <div className="payment-method-header">
          <div className="payment-method-info">
            <div className="payment-icon">💳</div>
            <div>
              <h2>BOG iPay</h2>
              <p>Accept credit/debit cards via Bank of Georgia</p>
            </div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={bogSettings.enabled}
              onChange={handleBogToggle}
              disabled={saving}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        {bogSettings.enabled && (
          <div className="payment-method-body">
            <div className="form-grid">
              <div className="form-group">
                <Input
                  label="Client ID"
                  value={bogSettings.settings.client_id || ''}
                  onChange={(e) => setBogSettings({
                    ...bogSettings,
                    settings: { ...bogSettings.settings, client_id: e.target.value }
                  })}
                  placeholder="10003181"
                />
              </div>
              <div className="form-group">
                <Input
                  label="Secret Key"
                  type="password"
                  value={bogSettings.settings.secret_key || ''}
                  onChange={(e) => setBogSettings({
                    ...bogSettings,
                    settings: { ...bogSettings.settings, secret_key: e.target.value }
                  })}
                  placeholder="Enter secret key"
                />
              </div>
            </div>
            <div className="payment-method-actions">
              <Button onClick={handleBogSave} loading={saving}>
                Save BOG Settings
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bank Transfer Settings */}
      <div className="payment-method-card">
        <div className="payment-method-header">
          <div className="payment-method-info">
            <div className="payment-icon">🏦</div>
            <div>
              <h2>Bank Transfer</h2>
              <p>Accept manual bank transfers with IBAN deep link</p>
            </div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={bankSettings.enabled}
              onChange={handleBankToggle}
              disabled={saving}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        {bankSettings.enabled && (
          <div className="payment-method-body">
            <div className="form-grid">
              <div className="form-group">
                <Input
                  label="Bank Name"
                  value={bankSettings.settings.bank_name || ''}
                  onChange={(e) => setBankSettings({
                    ...bankSettings,
                    settings: { ...bankSettings.settings, bank_name: e.target.value }
                  })}
                  placeholder="Bank of Georgia"
                />
              </div>
              <div className="form-group">
                <Input
                  label="Account Holder Name"
                  value={bankSettings.settings.account_holder || ''}
                  onChange={(e) => setBankSettings({
                    ...bankSettings,
                    settings: { ...bankSettings.settings, account_holder: e.target.value }
                  })}
                  placeholder="Your Company Name"
                />
              </div>
              <div className="form-group">
                <Input
                  label="IBAN"
                  value={bankSettings.settings.iban || ''}
                  onChange={(e) => setBankSettings({
                    ...bankSettings,
                    settings: { ...bankSettings.settings, iban: e.target.value }
                  })}
                  placeholder="GE00BG0000000000000000"
                />
              </div>
              <div className="form-group">
                <Input
                  label="SWIFT Code"
                  value={bankSettings.settings.swift || ''}
                  onChange={(e) => setBankSettings({
                    ...bankSettings,
                    settings: { ...bankSettings.settings, swift: e.target.value }
                  })}
                  placeholder="BAGAGE22"
                />
              </div>
            </div>

            <div className="bank-info-preview">
              <h3>Customer Will See:</h3>
              <div className="bank-details">
                <div className="bank-detail-row">
                  <span>Bank:</span>
                  <strong>{bankSettings.settings.bank_name || '-'}</strong>
                </div>
                <div className="bank-detail-row">
                  <span>Account Holder:</span>
                  <strong>{bankSettings.settings.account_holder || '-'}</strong>
                </div>
                <div className="bank-detail-row">
                  <span>IBAN:</span>
                  <strong>{bankSettings.settings.iban || '-'}</strong>
                </div>
                <div className="bank-detail-row">
                  <span>SWIFT:</span>
                  <strong>{bankSettings.settings.swift || '-'}</strong>
                </div>
              </div>
            </div>

            <div className="payment-method-actions">
              <Button onClick={handleBankSave} loading={saving}>
                Save Bank Transfer Settings
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSettings;